<!-- eslint-disable vue/singleline-html-element-content-newline -->
<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="expenses-table-wrapper">
    <v-skeleton-loader
      v-if="isMounting"
      type="table"
    />

    <v-data-table
      v-else
      :loading="isLoading"
      :headers="headers"
      :items="items"
      no-data-text="データがありません"
      loading-text="読込中..."
      :header-props="{ sortByText: 'ソート' }"
      hide-default-footer
      :items-per-page="-1"
      class="expenses-table"
    >
      <template #top>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>諸経費</v-toolbar-title>
          <v-spacer />
          <v-btn
            v-if="!readonly"
            color="primary"
            :ripple="false"
            small
            @click="createExpenseRow"
          >
            <v-icon left small>{{ icons.mdiPlus }}</v-icon>
            追加
          </v-btn>
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            class="ml-2 mr-1"
            @click="reloadExpenses"
          >
            <v-icon>{{ icons.mdiReload }}</v-icon>
          </v-btn>
          <v-divider
            vertical
            inset
            class="mx-2"
          />
          <v-menu
            bottom
            left
          >
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                icon
                v-bind="menuAttrs"
                v-on="menuOn"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <template #default="menu">
              <v-list
                :disabled="isLoading"
                dense
              >
                <v-subheader>操作</v-subheader>
                <v-list-item
                  @click="showTertiaryColumns = !showTertiaryColumns; menu.value = false"
                >
                  <v-list-item-title>
                    {{ showTertiaryColumns ? '詳細分類を非表示' : '詳細分類を表示' }}
                  </v-list-item-title>
                </v-list-item>

                <DataTableCopyListItemBtn
                  :disabled="isLoading"
                  :headers="headers"
                  :items="copyItems"
                />
              </v-list>
            </template>
          </v-menu>
        </v-toolbar>
      </template>

      <template
        v-for="obj in [
          {
            key: 'expensePrimaryLabelId',
            placeholder: '選択してください',
            required: true,
          },
          { key: 'expenseSecondaryLabelId' },
          { key: 'expenseTertiaryLabelId' },
          { key: 'expenseQuaternaryLabelId' },
        ]"
        #[`item.${obj.key}`]="{ item }"
      >
        <v-combobox
          v-if="!readonly"
          :key="`combobox-${obj.key}-${item.id}`"
          :value="selectedLabelName(item, obj.key)"
          :items="labelOptionsFor(item, obj.key)"
          item-text="attributes.name"
          :placeholder="obj.placeholder"
          :clearable="!obj.required"
          dense
          hide-details
          :menu-props="{ auto: true, offsetY: true }"
          :readonly="readonly"
          @change="onChangeLabel($event, item, obj.key)"
        />
        <v-text-field
          v-else
          :key="`text-field-${obj.key}-${item.id}`"
          :value="selectedLabelName(item, obj.key)"
          dense
          hide-details
          readonly
        />
      </template>

      <template #[`item.amount`]="{ item }">
        <v-text-field
          v-model.number="item.amount"
          type="number"
          dense
          hide-details
          :readonly="readonly"
          @change="debounceUpdateExpense(item, 'amount')"
        >
          <template #prepend-inner>¥</template>
        </v-text-field>
      </template>

      <template #[`item.taxRate`]="{ item }">
        <div v-if="!readonly">
          <v-select
            :value="item.taxRate"
            :items="taxRateOptions"
            item-text="text"
            item-value="value"
            dense
            hide-details
            @change="onTaxRateChange(item, $event)"
          />
          <div
            v-if="taxRateWarning(item)"
            class="warning--text text-caption"
          >
            ⚠ マスタ({{ getTaxRateText(resolveDefaultTaxRate(item)) }})と異なります
          </div>
        </div>
        <v-text-field
          v-else
          :value="getTaxRateText(item.taxRate)"
          dense
          hide-details
          readonly
        />
      </template>

      <template #[`item.taxAmount`]="{ item }">
        <v-text-field
          :value="displayTaxAmount(item)"
          dense
          hide-details
          readonly
        />
      </template>

      <template #[`item.expenseSupplierId`]="{ item }">
        <v-select
          v-if="!readonly"
          :value="item.expenseSupplierId"
          :items="supplierOptions"
          item-text="attributes.name"
          item-value="attributes.id"
          placeholder="未選択"
          clearable
          dense
          hide-details
          :menu-props="{ auto: true, offsetY: true }"
          @change="onChangeSupplier(item, $event)"
        />
        <v-text-field
          v-else
          :value="supplierNameFor(item)"
          dense
          hide-details
          readonly
        />
      </template>

      <template #[`item.supplierInvoiceCategory`]="{ item }">
        <v-select
          v-if="!readonly && !item.expenseSupplierId"
          :value="item.supplierInvoiceCategory || 'qualified'"
          :items="invoiceCategoryOptions"
          item-text="text"
          item-value="value"
          dense
          hide-details
          @change="onChangeInvoiceCategory(item, $event)"
        />
        <span v-else class="text-caption">{{ invoiceCategoryDisplay(item) }}</span>
      </template>

      <template #[`item.deductibleTaxAmount`]="{ item }">
        <v-text-field
          :value="displayDeductibleTaxAmount(item)"
          dense
          hide-details
          readonly
        />
      </template>

      <template #[`item.meta.isNonCash`]="{ item }">
        <v-checkbox
          v-model="item.meta.isNonCash"
          hide-details
          dense
          :readonly="readonly"
          style="width: 60px;"
          @change="updateExpense(item, { meta: { isNonCash: $event } })"
        />
      </template>

      <template #[`item.actions`]="{ item }">
        <div style="display: flex; gap: 8px; align-items: center; padding-top: 4px; margin-top: 4px;">
          <v-btn
            v-if="isNew(item) && !readonly"
            color="primary"
            :ripple="false"
            :disabled="!isValidExpense(item)"
            small
            @click="createExpense(item)"
          >
            保存
          </v-btn>

          <DeleteConfirmMenu
            v-if="!isNew(item) && !readonly"
            @confirm="destroyExpense(item)"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                text
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>{{ icons.mdiTrashCanOutline }}</v-icon>
              </v-btn>
            </template>
          </DeleteConfirmMenu>
        </div>
      </template>
    </v-data-table>

    <div
      v-if="!isMounting"
      class="text-caption text--secondary px-4 pb-2"
    >
      未登録取引先の控除率（対象日 {{ date }}）: {{ transitionalRate(date) }}% / {{ transitionalRateText(date) }}。
      税率・区分・控除対象額は保存時点の値で確定され、あとでマスタを変更しても保存済みの行は変わりません。
    </div>
  </div>
</template>

<script>
import {
  ref,
  toRefs,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiReload,
  mdiPlus,
  mdiDotsVertical,
  mdiTrashCanOutline,
} from '@mdi/js'
import { debounce } from 'lodash'

import ExpenseApi from '@/api/v2/eod-closing/Expense'
import ExpensePrimaryLabelApi from '@/api/v2/eod-closing/ExpensePrimaryLabel'
import ExpenseSecondaryLabelApi from '@/api/v2/eod-closing/ExpenseSecondaryLabel'
import ExpenseTertiaryLabelApi from '@/api/v2/eod-closing/ExpenseTertiaryLabel'
import ExpenseQuaternaryLabelApi from '@/api/v2/eod-closing/ExpenseQuaternaryLabel'
import ExpenseSupplierApi from '@/api/v2/eod-closing/ExpenseSupplier'
import useCompInit from '@/views/composable/useCompInit'
import useUserPreferences from '@/views/v2-temp/common/composables/useUserPreferences'
import useTaxCalculation from '@/views/v2-temp/report/expenses/composables/useTaxCalculation'
import useExpenseTable from '../composables/useExpenseTable'
import useInvoiceDeduction from '../composables/useInvoiceDeduction'
import DataTableCopyListItemBtn from '@/views/v2-temp/common/components/DataTableCopyListItemBtn.vue'
import DeleteConfirmMenu from '@/views/v2-temp/common/components/DeleteConfirmMenu.vue'

export default {
  components: {
    DataTableCopyListItemBtn,
    DeleteConfirmMenu,
  },
  props: {
    date: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { date } = toRefs(props)
    const { isLoading, initWith } = useCompInit()
    const { preferences } = useUserPreferences()
    const {
      taxRateOptions,
      normalizeTaxRate,
      toLegacyTaxRate,
      calculateTaxAmount,
      getTaxRateText,
      formatTaxAmount,
      getTaxAmountValue,
      getTaxAmountDisplay,
    } = useTaxCalculation()
    const {
      transitionalRate,
      transitionalRateText,
      calculateDeductibleTaxAmount,
    } = useInvoiceDeduction()

    const isMounting = ref(true)
    const expenses = ref([])
    const suppliersRaw = ref([])
    const primaryLabelsRaw = ref([])
    const secondaryLabelsRaw = ref([])
    const tertiaryLabelsRaw = ref([])
    const quaternaryLabelsRaw = ref([])
    const labelsRefByKey = {
      expensePrimaryLabelId: primaryLabelsRaw,
      expenseSecondaryLabelId: secondaryLabelsRaw,
      expenseTertiaryLabelId: tertiaryLabelsRaw,
      expenseQuaternaryLabelId: quaternaryLabelsRaw,
    }
    const apiByKey = {
      expensePrimaryLabelId: ExpensePrimaryLabelApi,
      expenseSecondaryLabelId: ExpenseSecondaryLabelApi,
      expenseTertiaryLabelId: ExpenseTertiaryLabelApi,
      expenseQuaternaryLabelId: ExpenseQuaternaryLabelApi,
    }

    const isNew = computed(() => item => !item.id)

    const showTertiaryColumns = computed({
      get: () => preferences.value['eod_closing:expenses'].showTertiaryColumns,
      set: val => {
        preferences.value['eod_closing:expenses'].showTertiaryColumns = val
      },
    })

    const {
      headers,
      primaryLabels,
      secondaryLabels,
      tertiaryLabels,
      quaternaryLabels,
      labelOptionsFor,
      resolveDefaultTaxRate,
      clearInvalidDescendants,
    } = useExpenseTable({
      showTertiaryColumns,
      includeActions: true,
      useColumnWidths: true,
      useLabelIds: true,
      includeSupplierColumns: true,
      labels: {
        primary: primaryLabelsRaw,
        secondary: secondaryLabelsRaw,
        tertiary: tertiaryLabelsRaw,
        quaternary: quaternaryLabelsRaw,
      },
    })

    const supplierOptions = computed(() => suppliersRaw.value)

    // 元のexpenseオブジェクト → 表示行のキャッシュ。
    // itemsは編集中の行オブジェクトを直接書き換える設計のため、expensesが更新される
    // (1行保存する等)たびに全行を作り直すと、他の未保存行の入力が初期値に戻ってしまう。
    // 元オブジェクトが同一の行はキャッシュを返して編集中の値を保持する
    const rowCache = new WeakMap()
    const items = computed(() => {
      return expenses.value.map(exp => {
        if (rowCache.has(exp)) return rowCache.get(exp)

        const attr = exp.attributes || {}
        const meta = exp.meta || {}
        const row = {
          id: attr.id,
          expensePrimaryLabelId: attr.expensePrimaryLabelId || null,
          expenseSecondaryLabelId: attr.expenseSecondaryLabelId || null,
          expenseTertiaryLabelId: attr.expenseTertiaryLabelId || null,
          expenseQuaternaryLabelId: attr.expenseQuaternaryLabelId || null,
          amount: attr.amount || 0,
          taxRate: attr.taxRate || 'blank',
          expenseSupplierId: attr.expenseSupplierId || null,
          // 保存時に確定したスナップショット(保存済み行はフロントで再計算しない)
          taxAmount: attr.taxAmount ?? null,
          deductibleTaxAmount: attr.deductibleTaxAmount ?? null,
          supplierInvoiceCategory: attr.supplierInvoiceCategory || null,
          deductionRatePercent: attr.deductionRatePercent ?? null,
          meta: {
            isNonCash: meta.isNonCash || false,
            tempId: meta.tempId,
          },
        }
        rowCache.set(exp, row)

        return row
      })
    })

    const selectedLabelName = computed(() => (item, key) => {
      const labelsRef = labelsRefByKey[key]
      if (!labelsRef) return ''

      const found = labelsRef.value.find(l => Number(l.attributes.id) === Number(item[key]))

      return found ? found.attributes.name : ''
    })

    /**
     * 消費税（概算）関連の表示ヘルパー。
     * 保存済み行はAPIが返すスナップショット値を表示し、未保存行のみプレビュー計算する
     */
    const supplierNameFor = item => {
      const supplier = suppliersRaw.value.find(s => Number(s.attributes.id) === Number(item.expenseSupplierId))

      return supplier ? supplier.attributes.name : ''
    }

    const resolvedCategoryFor = item => {
      if (item.expenseSupplierId) {
        // 取引先選択時はマスタ解決(保存済みはスナップショット)
        if (item.id) return item.supplierInvoiceCategory
        const supplier = suppliersRaw.value.find(s => Number(s.attributes.id) === Number(item.expenseSupplierId))

        return supplier ? supplier.attributes.invoiceCategory : 'qualified'
      }

      // 取引先未選択時は手動指定の区分(未指定=適格扱い)
      return item.supplierInvoiceCategory || 'qualified'
    }

    const invoiceCategoryDisplay = item => {
      if (item.taxRate === 'exempt') return '対象外'
      if (!item.taxRate || item.taxRate === 'blank') return ''

      const category = resolvedCategoryFor(item)
      if (category === 'unregistered') {
        const rate = item.id ? item.deductionRatePercent : transitionalRate(date.value)

        return rate === null || rate === undefined ? '未登録' : `未登録（経過措置 ${rate}%）`
      }

      return '適格（100%控除）'
    }

    const displayTaxAmount = item => {
      if (item.id && item.taxAmount !== null && item.taxAmount !== undefined) {
        return formatTaxAmount(item.taxAmount)
      }

      return getTaxAmountDisplay(item)
    }

    const displayDeductibleTaxAmount = item => {
      if (item.id) {
        return item.deductibleTaxAmount === null || item.deductibleTaxAmount === undefined
          ? '—'
          : formatTaxAmount(item.deductibleTaxAmount)
      }

      const taxAmount = calculateTaxAmount(item.amount, item.taxRate)
      const value = calculateDeductibleTaxAmount(taxAmount, item.taxRate, resolvedCategoryFor(item), date.value)

      return value === null ? '—' : formatTaxAmount(value)
    }

    const taxRateWarning = item => {
      const resolved = resolveDefaultTaxRate(item)

      return Boolean(resolved) && Boolean(item.taxRate) && item.taxRate !== 'blank' && item.taxRate !== resolved
    }

    // コピー用のデータ構造
    const copyItems = computed(() => {
      return items.value.map(item => {
        const foundOption = taxRateOptions.find(opt => opt.value === item.taxRate)
        const taxRateText = foundOption ? foundOption.text : ''
        const taxAmount = calculateTaxAmount(item.amount, item.taxRate)

        return {
          expensePrimaryLabelId: selectedLabelName.value(item, 'expensePrimaryLabelId') || '',
          expenseSecondaryLabelId: selectedLabelName.value(item, 'expenseSecondaryLabelId') || '',
          expenseTertiaryLabelId: selectedLabelName.value(item, 'expenseTertiaryLabelId') || '',
          expenseQuaternaryLabelId: selectedLabelName.value(item, 'expenseQuaternaryLabelId') || '',
          amount: item.amount,
          taxRate: taxRateText,
          taxAmount: taxAmount !== null ? taxAmount : '',
          expenseSupplierId: supplierNameFor(item) || '',
          supplierInvoiceCategory: invoiceCategoryDisplay(item) || '',
          deductibleTaxAmount: displayDeductibleTaxAmount(item) || '',
          'meta.isNonCash': item.meta.isNonCash ? '○' : '',
        }
      })
    })

    const isValidExpense = computed(() => item => {
      const {
        amount,
        expensePrimaryLabelId,
      } = item

      return typeof amount === 'number' && amount !== 0 && expensePrimaryLabelId
    })

    /**
     * API: 諸経費一覧取得
     */
    /**
     * APIレスポンスのtaxRateを正規化（旧キー名を新キー名に変換）
     * TODO: バックエンドリリース完了後に削除
     */
    const normalizeExpenseData = expense => ({
      ...expense,
      attributes: {
        ...expense.attributes,
        taxRate: normalizeTaxRate(expense.attributes?.taxRate),
      },
    })

    const getExpenses = async () => {
      isLoading.value = true
      try {
        const res = await ExpenseApi.getExpenses(date.value)
        if (res.data.status === 'success') {
          expenses.value = res.data.data.data.map(normalizeExpenseData)
        }
      } finally {
        isLoading.value = false
      }
    }

    const getExpenseSuppliers = async () => {
      const res = await ExpenseSupplierApi.getSuppliers()
      if (res.data.status === 'success') {
        suppliersRaw.value = res.data.data.data
      }
    }

    const getExpenseLabels = async () => {
      isLoading.value = true
      try {
        const [
          resPrimary,
          resSecondary,
          resTertiary,
          resQuaternary,
        ] = await Promise.all([
          ExpensePrimaryLabelApi,
          ExpenseSecondaryLabelApi,
          ExpenseTertiaryLabelApi,
          ExpenseQuaternaryLabelApi,
        ].map(api => api.getLabels()))
        const resArr = [resPrimary, resSecondary, resTertiary, resQuaternary]
        resArr.forEach((res, idx) => {
          if (res.data.status === 'success') {
            switch (idx) {
              case 0:
                primaryLabelsRaw.value = res.data.data.data
                break
              case 1:
                secondaryLabelsRaw.value = res.data.data.data
                break
              case 2:
                tertiaryLabelsRaw.value = res.data.data.data
                break
              case 3:
                quaternaryLabelsRaw.value = res.data.data.data
                break
              default:
                break
            }
          }
        })
      } finally {
        isLoading.value = false
      }
    }

    /**
     * データテーブルの行追加 (新規Expenseの作成)
     * - ここでは即座にAPIで作成するか、あるいはローカルだけに追加するか選べる
     * - 例では即API呼び出し→テーブル再取得 にしている
     */
    const buildEmptyExpense = () => ({
      id: null,
      attributes: {
        expensePrimaryLabelId: null,
        expenseSecondaryLabelId: null,
        expenseTertiaryLabelId: null,
        expenseQuaternaryLabelId: null,
        amount: 0,
        taxRate: 'blank',
        expenseSupplierId: null,
      },
      meta: {
        isNonCash: false,
        tempId: `temp_${Date.now()}_${Math.floor(Math.random() * 1e9)}`,
      },
    })
    const createExpenseRow = async () => {
      expenses.value = [buildEmptyExpense(), ...expenses.value]
    }
    const createExpense = async item => {
      try {
        isLoading.value = true

        const {
          amount,
          expensePrimaryLabelId,
          expenseSecondaryLabelId,
          expenseTertiaryLabelId,
          expenseQuaternaryLabelId,
          taxRate,
          expenseSupplierId,
          supplierInvoiceCategory,
          meta,
        } = item

        const res = await ExpenseApi.createExpense({
          businessDate: date.value,
          amount,
          expensePrimaryLabelId,
          expenseSecondaryLabelId,
          expenseTertiaryLabelId,
          expenseQuaternaryLabelId,
          taxRate: toLegacyTaxRate(taxRate),
          expenseSupplierId,
          supplierInvoiceCategory,
          meta: {
            isNonCash: meta.isNonCash,
          },
        })

        if (res.data.status === 'success') {
          const created = normalizeExpenseData(res.data.data.data)
          const idx = expenses.value.findIndex(e => e.meta.tempId === item.meta.tempId)
          if (idx >= 0) {
            expenses.value.splice(idx, 1, created)
          }
          emit('changed')
        }
      } catch (error) {
        error.response?.data?.message?.forEach(msg => {
          vm.$toast.error(msg)
        })
      } finally {
        isLoading.value = false
      }
    }

    /**
     * フィールド更新(部分更新) - debounce
     */
    const updateExpense = async (item, params = {}) => {
      if (!item.id) return

      const {
        amount,
        expensePrimaryLabelId,
        expenseSecondaryLabelId,
        expenseTertiaryLabelId,
        expenseQuaternaryLabelId,
        taxRate,
        expenseSupplierId,
        supplierInvoiceCategory,
      } = item

      try {
        const res = await ExpenseApi.updateExpense(item.id, {
          amount,
          expensePrimaryLabelId,
          expenseSecondaryLabelId,
          expenseTertiaryLabelId,
          expenseQuaternaryLabelId,
          taxRate: toLegacyTaxRate(taxRate),
          expenseSupplierId,
          supplierInvoiceCategory,
          ...params,
        })
        if (res.data.status === 'success') {
          const updated = normalizeExpenseData(res.data.data.data)
          const idx = expenses.value.findIndex(e => e.id === updated.id)
          if (idx >= 0) {
            expenses.value.splice(idx, 1, updated)
          }
          emit('changed')
        }
      } catch (error) {
        error.response?.data?.message?.forEach(msg => {
          vm.$toast.error(msg)
        })
      }
    }
    // 行×フィールドごとに独立したdebounce。
    // 共有debounceだと500ms以内に別の行・別の項目を編集したとき、先に保留していた保存がキャンセルされ消失する
    const debouncedUpdaters = {}
    const debounceUpdateExpense = (item, key) => {
      if (!item.id) return

      const updaterKey = `${item.id}:${key}`
      if (!debouncedUpdaters[updaterKey]) {
        debouncedUpdaters[updaterKey] = debounce((targetItem, targetKey) => {
          const params = { [`${targetKey}`]: targetItem[targetKey] }
          updateExpense(targetItem, params)
        }, 500)
      }
      debouncedUpdaters[updaterKey](item, key)
    }

    /**
     * 税率変更時の処理
     * @param {Object} item - 経費アイテム
     * @param {string} taxRate - 選択された税率
     */
    const onTaxRateChange = (item, taxRate) => {
      // eslint-disable-next-line no-param-reassign
      item.taxRate = taxRate
      debounceUpdateExpense(item, 'taxRate')
    }

    /**
     * ラベル選択変更の副作用:
     * - 選択中の親と矛盾する子孫の選択のみクリア(共通ラベルの子選択は維持)
     * - 品目マスタの税率を自動セット(deepest-wins)。ただし手動上書き(直前の自動値と異なる税率)は尊重して変更しない
     */
    /* eslint-disable no-param-reassign */
    const applyLabelSideEffects = (item, key, previousResolvedTaxRate) => {
      clearInvalidDescendants(item, key)

      const resolved = resolveDefaultTaxRate(item)
      const manualOverride = item.taxRate
        && item.taxRate !== 'blank'
        && item.taxRate !== previousResolvedTaxRate
      if (resolved && !manualOverride) {
        item.taxRate = resolved
      }
    }
    /* eslint-enable no-param-reassign */

    /**
     * 新規ラベル作成(現場追加)。選択中の上位分類を親として自動セットしclub所有で作成される
     */
    const PARENT_KEY_BY_CHILD = {
      expenseSecondaryLabelId: 'expensePrimaryLabelId',
      expenseTertiaryLabelId: 'expenseSecondaryLabelId',
      expenseQuaternaryLabelId: 'expenseTertiaryLabelId',
    }
    const createNewLabel = async (key, name, item = null) => {
      const api = apiByKey[key]
      if (!api) return null

      try {
        const parentKey = PARENT_KEY_BY_CHILD[key]
        const parentId = parentKey ? item?.[parentKey] ?? null : null
        const res = parentKey ? await api.createLabel(name, parentId) : await api.createLabel(name)
        if (res.data.status === 'success') {
          const labelsRef = labelsRefByKey[key]
          if (!labelsRef) return null

          const label = res.data.data.data
          labelsRef.value = [...labelsRef.value, label]

          return label
        }
      } catch (e) {
        console.error(e)
      }

      return null
    }
    /**
     * コンボボックス（各ラベル）の @change ハンドラ
     * - ラベルobj か 文字列(=新規作成候補) かを判定
     * - 新規なら createNewLabel(key, label)
     * - その後 expense を更新
     */
    const onChangeLabel = async (label, item, key) => {
      // 変更前の自動解決値(手動上書きの判定に使う)
      const previousResolvedTaxRate = resolveDefaultTaxRate(item)

      if (typeof label === 'string') { // ラベルを新規作成
        const newLabel = await createNewLabel(key, label, item)
        if (newLabel) {
          // eslint-disable-next-line no-param-reassign
          item[key] = newLabel.id
          applyLabelSideEffects(item, key, previousResolvedTaxRate)

          return debounceUpdateExpense(item, key)
        }
      } else if (label === null) { // 選択解除
        // eslint-disable-next-line no-param-reassign
        item[key] = null
        applyLabelSideEffects(item, key, previousResolvedTaxRate)

        return debounceUpdateExpense(item, key)
      }

      // eslint-disable-next-line no-param-reassign
      item[key] = label.id
      applyLabelSideEffects(item, key, previousResolvedTaxRate)

      return debounceUpdateExpense(item, key)
    }

    /**
     * 取引先変更
     */
    const onChangeSupplier = (item, supplierId) => {
      // eslint-disable-next-line no-param-reassign
      item.expenseSupplierId = supplierId || null
      debounceUpdateExpense(item, 'expenseSupplierId')
    }

    /**
     * 区分の手動変更(取引先未選択時のみ)。取引先選択時はマスタ解決が優先される
     */
    const invoiceCategoryOptions = [
      { text: '適格', value: 'qualified' },
      { text: '未登録', value: 'unregistered' },
    ]
    const onChangeInvoiceCategory = (item, category) => {
      // eslint-disable-next-line no-param-reassign
      item.supplierInvoiceCategory = category
      debounceUpdateExpense(item, 'supplierInvoiceCategory')
    }

    /**
     * 行削除
     */
    const destroyExpense = async item => {
      if (!item.id) return

      try {
        isLoading.value = true
        const res = await ExpenseApi.destroyExpense(item.id)
        if (res.data.status === 'success') {
          expenses.value = expenses.value.filter(e => Number(e.id) !== Number(item.id))
          emit('changed')
        }
      } catch (error) {
        error.response?.data?.message?.forEach(msg => {
          vm.$toast.error(msg)
        })
      } finally {
        isLoading.value = false
      }
    }

    const setInitialExpenses = () => {
      if (expenses.value.length === 0 && !props.readonly) {
        expenses.value = [...Array(10)].map(_ => buildEmptyExpense())
      }
    }

    const reloadExpenses = async () => {
      await Promise.all([
        getExpenses(),
        getExpenseLabels(),
        getExpenseSuppliers(),
      ])
      setInitialExpenses()
    }

    onMounted(async () => {
      await initWith([
        getExpenses(),
        getExpenseLabels(),
        getExpenseSuppliers(),
      ])
      setInitialExpenses()
      isMounting.value = false
    })

    return {
      // refs
      isMounting,
      expenses,
      primaryLabels,
      secondaryLabels,
      tertiaryLabels,
      quaternaryLabels,
      showTertiaryColumns,

      // constants
      taxRateOptions,

      // computed
      selectedLabelName,
      items,
      headers,
      copyItems,
      isNew,
      isValidExpense,

      // methods
      createExpenseRow,
      createExpense,
      updateExpense,
      destroyExpense,
      reloadExpenses,
      onChangeLabel,
      createNewLabel,
      debounceUpdateExpense,
      onTaxRateChange,
      getTaxRateText,
      calculateTaxAmount,
      formatTaxAmount,
      getTaxAmountValue,
      getTaxAmountDisplay,

      // 消費税(概算): 取引先・控除対象額
      supplierOptions,
      labelOptionsFor,
      resolveDefaultTaxRate,
      onChangeSupplier,
      invoiceCategoryOptions,
      onChangeInvoiceCategory,
      supplierNameFor,
      invoiceCategoryDisplay,
      displayTaxAmount,
      displayDeductibleTaxAmount,
      taxRateWarning,
      transitionalRate,
      transitionalRateText,

      // from useCompInit
      isLoading,

      // icons
      icons: {
        mdiReload,
        mdiPlus,
        mdiDotsVertical,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

@include theme--child(expenses-table) using ($material) {
  th.fixed-cell,
  td.fixed-cell {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 3;
    background: map-deep-get($material, 'app-bar');
  }

  th.fixed-cell {
    z-index: 4;
  }

  .v-progress-linear {
    z-index: 5;
  }
}

.expenses-table-wrapper {
  overflow-x: auto;
}

.expenses-table {
  white-space: nowrap;
}
</style>
